/**
 * AppModeContext — Global mode resolver
 *
 * appMode = 'demo' | 'real'
 * Source of truth: localStorage flag set during Quick Demo ("Existing User") login.
 * Expose via useAppMode().
 *
 * Reactivity contract
 * ───────────────────
 * The provider MUST react to changes made by `setDemoMode()` /
 * `clearDemoMode()` that happen AFTER mount — otherwise the app boots with
 * `mode='real'`, the Quick-Demo auth flow writes the flag, and the stale
 * provider value causes the scheduler and other surfaces to incorrectly
 * pick the real-mode code path and call `/api/*`. We use a subscription
 * pattern (storage events + a synthetic in-tab event `ariaone-mode-change`)
 * so cross-tab and same-tab flips both propagate immediately.
 */
import {
  createContext, useContext, useCallback, useEffect, useMemo, useState,
} from 'react';

const AppModeContext = createContext({ mode: 'real', isDemo: false });

const DEMO_KEY = 'ariaone-demo-mode';
const MODE_CHANGE_EVENT = 'ariaone-mode-change';

const readMode = () => {
  try {
    return localStorage.getItem(DEMO_KEY) === 'true' ? 'demo' : 'real';
  } catch {
    return 'real';
  }
};

export const AppModeProvider = ({ children }) => {
  // Reactive state — initial read happens at mount; subscription below keeps
  // it in sync with any subsequent setDemoMode()/clearDemoMode() call or
  // cross-tab localStorage change.
  const [mode, setMode] = useState(readMode);

  useEffect(() => {
    const refresh = () => setMode(readMode());
    const onStorage = (e) => {
      if (!e || e.key === null || e.key === DEMO_KEY) refresh();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(MODE_CHANGE_EVENT, refresh);
    // Guard against race: auth flow may have toggled the flag between
    // provider mount and this effect registration.
    refresh();
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(MODE_CHANGE_EVENT, refresh);
    };
  }, []);

  const value = useMemo(() => ({
    mode,
    isDemo: mode === 'demo',
    isReal: mode === 'real',
  }), [mode]);

  return (
    <AppModeContext.Provider value={value}>
      {children}
    </AppModeContext.Provider>
  );
};

const notifyModeChange = () => {
  try { window.dispatchEvent(new Event(MODE_CHANGE_EVENT)); } catch { /* SSR */ }
};

/** Set demo mode flag (called from auth flow). */
export const setDemoMode = (isDemo) => {
  try {
    if (isDemo) localStorage.setItem(DEMO_KEY, 'true');
    else localStorage.removeItem(DEMO_KEY);
    notifyModeChange();
  } catch { /* SSR safety */ }
};

/** Clear demo mode flag (called on sign out). */
export const clearDemoMode = () => {
  try { localStorage.removeItem(DEMO_KEY); notifyModeChange(); } catch {}
};

export const useAppMode = () => useContext(AppModeContext);

export default AppModeContext;
